import React from "react";
import { Link } from "react-router-dom";
import {
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
} from "lucide-react";
import { LogoIcon } from "../Icons";
import { useSiteInfo } from "../../hooks/useSiteInfo";
import { useLanguage } from "../../contexts/LanguageContext";
import { useNavigationItems } from "../../hooks/useNavigationItems";
import { pickNav } from "../../helpers/contentHelpers";

const Footer = () => {
  const { info } = useSiteInfo();
  const { t, language } = useLanguage();

  const { items: legalLinks } = useNavigationItems("footer-legal");

  const socialDefs = [
    { key: "linkedin_link", Icon: Linkedin, label: "LinkedIn" },
    { key: "facebook_link", Icon: Facebook, label: "Facebook" },
    { key: "insta_link", Icon: Instagram, label: "Instagram" },
    { key: "twitter_link", Icon: Twitter, label: "Twitter / X" },
  ];
  const socialLinks = socialDefs.filter((s) => info(s.key));

  return (
    <footer className="bg-subidha-blue text-white pt-12 pb-8 overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8 mb-10 text-center md:text-left">
          {/* Brand & Info */}
          <div className="space-y-4 max-w-sm flex flex-col items-center md:items-start">
            <LogoIcon className="h-10 w-auto text-white" />
            <p className="text-slate-400 text-sm leading-relaxed">
              {t("footer.description")}
            </p>
          </div>

          {/* Licenses Info (Middle) */}
          <div className="flex flex-col space-y-2 text-xs font-bold text-slate-300 uppercase tracking-widest md:mx-auto">
            <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-lg text-center">
              <span>{info("license_number", t("footer.licenseNumber"))}</span>
            </div>
            <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-lg text-center">
              <span>{info("dispatch_number", t("footer.dispatchNumber"))}</span>
            </div>
          </div>

          {/* Social Links & Admin Actions */}
          <div className="flex flex-col items-center md:items-end space-y-5">
            {socialLinks.length > 0 && (
              <div className="flex space-x-3">
                {socialLinks.map(({ key, Icon, label }) => (
                  <a
                    key={key}
                    href={info(key)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-10 h-10 bg-white/5 hover:bg-accent-green rounded-full flex items-center justify-center transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
            
            {/* Admin Login Button */}
            <Link
              to="/login"
              className="text-[10px] font-bold uppercase tracking-widest border border-slate-600 hover:border-accent-green hover:bg-white/5 text-slate-300 hover:text-white px-5 py-2 rounded-lg transition-all"
            >
              Admin Portal
            </Link>
          </div>
        </div>

        {/* Bottom Bar items minimal layout */}
        <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center text-[10px] font-black tracking-[0.15em] text-slate-500 uppercase space-y-4 md:space-y-0">
          <div className="flex flex-col items-center md:items-start space-y-1">
            <p>© {new Date().getFullYear()} SUBIDHA INC. TOKYO, JAPAN.</p>
            <p className="text-slate-600">{info("registered_address", t("footer.registeredAddress"))}</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {legalLinks.length > 0
              ? legalLinks
                  .filter((item) => item.isPublished !== false)
                  .map((item) => {
                    const picked = pickNav(item, language);
                    if (picked.linkType === "internal") {
                      return (
                        <Link
                          key={item.id}
                          to={picked.url}
                          className="hover:text-accent-green cursor-pointer transition-colors"
                        >
                          {picked.label}
                        </Link>
                      );
                    }
                    return (
                      <span
                        key={item.id}
                        className="hover:text-accent-green cursor-pointer transition-colors"
                      >
                        {picked.label}
                      </span>
                    );
                  })
              : (
                <>
                  <Link to="/privacy-policy" className="hover:text-accent-green cursor-pointer transition-colors">
                    {t("footer.bottomBar.privacy")}
                  </Link>
                  <Link to="/terms-of-service" className="hover:text-accent-green cursor-pointer transition-colors">
                    {t("footer.bottomBar.terms")}
                  </Link>
                  <Link to="/cookie-policy" className="hover:text-accent-green cursor-pointer transition-colors">
                    {t("footer.bottomBar.cookies")}
                  </Link>
                </>
              )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
